import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill } from "@/components/StatusPill";
import { CheckCircle2, Lock, MoreHorizontal, Pencil, Trash2, Filter, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// Build a human-friendly campaign label:  BWZ-YYYY-Client-City-Pod###
// Keeps the stable campaign_id intact but surfaces a descriptive label in the UI.
export function campaignDisplayLabel(c) {
  if (!c) return "";
  const year = (c.start_date || c.created_at || "").slice(0, 4) || new Date().getFullYear();
  const client = (c.client_name || "").trim().replace(/\s+/g, " ");
  const city = (c.city || "").trim().replace(/\s+/g, " ");
  // Pull trailing pod number if format is like "BLR Pod-1", else use the pod string as-is
  const pod = (c.pod || "").trim();
  const podNum = (pod.match(/(\d+)\s*$/) || [])[1];
  const podLabel = podNum ? `Pod-${podNum}` : pod;
  const parts = ["BWZ", year, client, city, podLabel].filter((x) => x && String(x).length > 0);
  return parts.join("-");
}

export default function Execution() {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [recentUpdates, setRecentUpdates] = useState([]);
  const [plannings, setPlannings] = useState([]);
  const [editing, setEditing] = useState(null);
  const [duplicateOf, setDuplicateOf] = useState(null); // { existing_id, update_date, campaign_id }
  const [filter, setFilter] = useState({ campaign_id: "all", date: "", risk: "all" });
  const [form, setForm] = useState({
    campaign_id: "",
    update_date: new Date().toISOString().slice(0, 10),
    installations_planned: 10,
    installations_completed_t: 0,
    installations_completed_nt: 0,
    pending_onsite_count: 0,
    pending_onsite_touched: false,
    remarks: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    const [c, e] = await Promise.all([api.get("/campaigns"), api.get("/executions", { params: { limit: 200 } })]);
    setCampaigns(c.data);
    setRecentUpdates(e.data);
    if (c.data[0] && !form.campaign_id) setForm((f) => ({ ...f, campaign_id: c.data[0].campaign_id }));
  };

  useEffect(() => { refresh(); }, []);

  // Load plannings for the selected campaign so we can auto-fill Planned for the chosen date
  useEffect(() => {
    if (!form.campaign_id) { setPlannings([]); return; }
    api.get("/plannings", { params: { campaign_id: form.campaign_id } })
      .then((r) => setPlannings(r.data))
      .catch(() => setPlannings([]));
  }, [form.campaign_id]);

  // Look up planning for the chosen date
  const matchedPlanning = useMemo(() => {
    return plannings.find((p) => p.planned_date === form.update_date);
  }, [plannings, form.update_date]);
  const plannedLocked = !!matchedPlanning && (matchedPlanning.daily_target ?? 0) > 0;

  // When the campaign changes and the current date has NO matching planning slot,
  // auto-jump to the best slot: today's slot if it exists, else the next upcoming slot,
  // else the most recent past slot. This is what makes "select campaign → Planned auto-fills".
  useEffect(() => {
    if (!form.campaign_id || plannings.length === 0) return;
    const todayIso = new Date().toISOString().slice(0, 10);
    const has = plannings.some((p) => p.planned_date === form.update_date);
    if (has) return;
    const sorted = [...plannings].sort((a, b) => a.planned_date.localeCompare(b.planned_date));
    const target = sorted.find((p) => p.planned_date === todayIso)
      || sorted.find((p) => p.planned_date >= todayIso)
      || sorted[sorted.length - 1];
    if (target && target.planned_date !== form.update_date) {
      setForm((f) => ({ ...f, update_date: target.planned_date, pending_onsite_touched: false }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plannings]);

  // Auto-fill 'planned' from planning daily_target when matched
  useEffect(() => {
    if (matchedPlanning && (matchedPlanning.daily_target ?? 0) > 0) {
      setForm((f) => ({ ...f, installations_planned: matchedPlanning.daily_target }));
    }
  }, [matchedPlanning]);

  // Auto-compute pending_onsite = planned - T - NT until user touches that field.
  // Allowed to be negative (means over-delivered).
  useEffect(() => {
    setForm((f) => {
      if (f.pending_onsite_touched) return f;
      const planned = Number(f.installations_planned) || 0;
      const t = Number(f.installations_completed_t) || 0;
      const nt = Number(f.installations_completed_nt) || 0;
      const computed = planned - t - nt;
      if (computed === f.pending_onsite_count) return f;
      return { ...f, pending_onsite_count: computed };
    });
  }, [form.installations_planned, form.installations_completed_t, form.installations_completed_nt, form.pending_onsite_touched]);

  const planned = Number(form.installations_planned) || 0;
  const ct = Number(form.installations_completed_t) || 0;
  const cnt = Number(form.installations_completed_nt) || 0;
  const onsite = Number(form.pending_onsite_count) || 0;
  const completed = ct + cnt;
  const overflow = completed > planned;

  const formError = "";  // Completed is now allowed to exceed Planned per business rule

  const campaignById = useMemo(() => {
    const m = {};
    for (const c of campaigns) m[c.campaign_id] = c;
    return m;
  }, [campaigns]);

  const filteredUpdates = useMemo(() => {
    return recentUpdates.filter((e) => {
      if (filter.campaign_id !== "all" && e.campaign_id !== filter.campaign_id) return false;
      if (filter.date && e.update_date !== filter.date) return false;
      if (filter.risk === "risk" && !e.predictive_risk_flag) return false;
      if (filter.risk === "ok" && e.predictive_risk_flag) return false;
      return true;
    });
  }, [recentUpdates, filter]);

  const submit = async (ev) => {
    ev.preventDefault();
    if (!form.campaign_id) { toast.error("Pick a campaign"); return; }
    if (formError) { toast.error(formError); return; }
    setSubmitting(true);
    try {
      await api.post("/executions", {
        campaign_id: form.campaign_id,
        update_date: form.update_date,
        installations_planned: planned,
        installations_completed_t: ct,
        installations_completed_nt: cnt,
        pending_onsite_count: onsite,
        remarks: form.remarks,
      });
      toast.success("Daily update logged");
      setDuplicateOf(null);
      setForm((f) => ({
        ...f,
        installations_completed_t: 0,
        installations_completed_nt: 0,
        pending_onsite_count: 0,
        pending_onsite_touched: false,
        remarks: "",
      }));
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "duplicate_execution") {
        setDuplicateOf(detail);
        toast.error(detail.message || "A duplicate daily update exists for this date.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const editDuplicate = async () => {
    if (!duplicateOf?.existing_id) return;
    try {
      const r = await api.get("/executions", { params: { campaign_id: duplicateOf.campaign_id, from_date: duplicateOf.update_date, to_date: duplicateOf.update_date } });
      const row = (r.data || []).find((x) => x.id === duplicateOf.existing_id) || (r.data || [])[0];
      if (row) { setEditing(row); setDuplicateOf(null); }
      else toast.error("Could not find the existing entry.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const pct = planned ? Math.round((completed / planned) * 100) : 0;
  const riskFlag = pct < 70 && planned > 0;

  const canEdit = user && ["admin", "command_center", "ado", "pod_lead"].includes(user.role);
  const canDelete = user && ["admin", "command_center"].includes(user.role);

  const removeUpdate = async (id) => {
    if (!confirm("Delete this daily update? Campaign progress will be adjusted.")) return;
    try {
      await api.delete(`/executions/${id}`);
      toast.success("Daily update deleted");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="execution-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Daily Updates</p>
        <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Execution Log</h2>
        <p className="text-sm text-zinc-500 mt-1">
          Mobile-friendly daily progress entry — Trusted &amp; Non-Trusted fleet counts, with planned auto-filled from the planning board when a slot exists.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Update form */}
        <Card className="lg:col-span-1 p-5" data-testid="execution-form-card">
          <h3 className="font-display text-lg font-semibold mb-4">Log progress</h3>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Campaign</Label>
              <Select value={form.campaign_id} onValueChange={(v) => setForm({ ...form, campaign_id: v, pending_onsite_touched: false })}>
                <SelectTrigger data-testid="exec-campaign"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {campaigns.map((c) => (
                    <SelectItem key={c.campaign_id} value={c.campaign_id}>
                      {campaignDisplayLabel(c)}
                      <span className="text-zinc-400 ml-2">· {c.campaign_id}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Date</Label>
              <Input
                type="date"
                value={form.update_date}
                onChange={(e) => setForm({ ...form, update_date: e.target.value, pending_onsite_touched: false })}
                data-testid="exec-date"
              />
              {matchedPlanning && (
                <p className="text-[11px] text-emerald-700 mt-1" data-testid="exec-planning-match">
                  ✓ Planning slot found · Pod: <span className="font-semibold">{matchedPlanning.assigned_team}</span>
                  {plannedLocked ? <> · Daily target: <span className="font-mono font-semibold">{matchedPlanning.daily_target}</span></> : null}
                </p>
              )}
              {!matchedPlanning && form.campaign_id && (
                <p className="text-[11px] text-zinc-500 mt-1" data-testid="exec-no-planning">
                  No planning slot for this date — “Planned” is editable.
                </p>
              )}
              {plannings.length > 0 && (
                <div className="mt-2" data-testid="exec-planning-dates">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">Planned dates for this campaign</div>
                  <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                    {plannings.slice().sort((a, b) => a.planned_date.localeCompare(b.planned_date)).map((p) => {
                      const active = p.planned_date === form.update_date;
                      const done = (p.execution_status === "done" || p.execution_status === "over");
                      return (
                        <button
                          type="button"
                          key={p.id}
                          onClick={() => setForm({ ...form, update_date: p.planned_date, pending_onsite_touched: false })}
                          className={`text-[10px] px-1.5 py-0.5 rounded border font-mono transition-colors ${active ? "bg-zinc-900 text-white border-zinc-900" : done ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}
                          title={`Daily target: ${p.daily_target ?? 0} · ${p.assigned_team}`}
                          data-testid={`exec-date-chip-${p.planned_date}`}
                        >
                          {p.planned_date.slice(5)}{p.daily_target ? ` · ${p.daily_target}` : ""}{done ? " ✓" : ""}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {duplicateOf && duplicateOf.campaign_id === form.campaign_id && duplicateOf.update_date === form.update_date && (
                <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-800 flex items-start gap-2" data-testid="exec-duplicate-banner">
                  <div className="flex-1">
                    <div className="font-semibold">A daily update already exists for this date.</div>
                    <div className="text-red-700 mt-0.5">Edit the existing entry instead of creating a new one.</div>
                  </div>
                  <button type="button" onClick={editDuplicate} className="text-red-700 underline hover:text-red-900 font-semibold" data-testid="exec-edit-existing">Edit existing</button>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1">
                Planned
                {plannedLocked && <Lock className="h-3 w-3 text-zinc-500" />}
              </Label>
              <Input
                type="number"
                min={0}
                value={form.installations_planned}
                onChange={(e) => setForm({ ...form, installations_planned: e.target.value, pending_onsite_touched: false })}
                disabled={plannedLocked}
                className={plannedLocked ? "bg-zinc-100 text-zinc-700" : ""}
                data-testid="exec-planned"
              />
              {plannedLocked && (
                <p className="text-[10px] text-zinc-500 mt-0.5">
                  Auto-filled from planning. Edit the planning slot to change this.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Completed T <span className="text-zinc-400 font-normal">(Trusted)</span></Label>
                <Input
                  type="number"
                  min={0}
                  value={form.installations_completed_t}
                  onChange={(e) => setForm({ ...form, installations_completed_t: e.target.value, pending_onsite_touched: false })}
                  data-testid="exec-completed-t"
                />
              </div>
              <div>
                <Label className="text-xs">Completed N.T <span className="text-zinc-400 font-normal">(Non-Trusted)</span></Label>
                <Input
                  type="number"
                  min={0}
                  value={form.installations_completed_nt}
                  onChange={(e) => setForm({ ...form, installations_completed_nt: e.target.value, pending_onsite_touched: false })}
                  data-testid="exec-completed-nt"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Pending On-site <span className="text-zinc-400 font-normal">(auto = Planned − T − N.T, editable)</span></Label>
              <Input
                type="number"
                value={form.pending_onsite_count}
                onChange={(e) => setForm({ ...form, pending_onsite_count: e.target.value, pending_onsite_touched: true })}
                data-testid="exec-onsite"
              />
              {Number(form.pending_onsite_count) < 0 && (
                <p className="text-[10px] text-amber-700 mt-0.5">Negative — over-delivered by {Math.abs(Number(form.pending_onsite_count))}.</p>
              )}
            </div>
            {formError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="exec-form-error">
                {formError}
              </div>
            )}
            {overflow && !formError && (
              <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[11px] text-blue-700" data-testid="exec-overflow-note">
                Completed ({completed}) exceeds Planned ({planned}) — that's OK and recorded as is.
              </div>
            )}
            <div>
              <Label className="text-xs">Remarks / Blockers</Label>
              <Textarea rows={3} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="exec-remarks" placeholder="Material short, weather…"/>
            </div>

            {/* Live KPI preview */}
            <div className="flex items-center justify-between p-3 rounded-md bg-zinc-50 border border-zinc-200">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Achievement</div>
                <div className="font-display text-2xl font-bold" data-testid="exec-pct">{pct}%</div>
                <div className="text-[10px] text-zinc-500 font-mono">{completed} of {planned}</div>
              </div>
              {riskFlag ? <StatusPill status="red">Below 70% — at risk</StatusPill> : <StatusPill status="green">On target</StatusPill>}
            </div>

            <Button type="submit" disabled={submitting || !!formError} className="w-full bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="exec-submit">
              <CheckCircle2 className="h-4 w-4 mr-2" /> {submitting ? "Saving…" : "Submit Update"}
            </Button>
          </form>
        </Card>

        {/* Recent updates */}
        <Card className="lg:col-span-2 overflow-hidden" data-testid="recent-updates-card">
          <div className="px-5 py-3 border-b border-zinc-200 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-display text-lg font-semibold">Recent updates</h3>
                <p className="text-xs text-zinc-500">Filter to zero in on a specific campaign, date or risk level</p>
              </div>
              {(filter.campaign_id !== "all" || filter.date || filter.risk !== "all") && (
                <button
                  onClick={() => setFilter({ campaign_id: "all", date: "", risk: "all" })}
                  className="text-[11px] text-zinc-500 hover:text-zinc-900 flex items-center gap-1"
                  data-testid="clear-updates-filter"
                >
                  <X className="h-3 w-3" /> Clear filters
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1"><Filter className="h-3 w-3" /> Campaign</Label>
                <Select value={filter.campaign_id} onValueChange={(v) => setFilter({ ...filter, campaign_id: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid="updates-filter-campaign"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All campaigns</SelectItem>
                    {campaigns.map((c) => (
                      <SelectItem key={c.campaign_id} value={c.campaign_id}>{campaignDisplayLabel(c)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Date</Label>
                <Input type="date" value={filter.date} onChange={(e) => setFilter({ ...filter, date: e.target.value })} className="h-8 text-xs" data-testid="updates-filter-date" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Risk</Label>
                <Select value={filter.risk} onValueChange={(v) => setFilter({ ...filter, risk: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid="updates-filter-risk"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="risk">Risk only</SelectItem>
                    <SelectItem value="ok">OK only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b">
                <tr>
                  <th className="text-left py-2.5 px-3 font-semibold">Date</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Campaign</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Plan</th>
                  <th className="text-left py-2.5 px-3 font-semibold">T</th>
                  <th className="text-left py-2.5 px-3 font-semibold">N.T</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Total Pending</th>
                  <th className="text-left py-2.5 px-3 font-semibold">%</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Risk</th>
                  {canEdit && <th className="text-left py-2.5 px-3 font-semibold w-12"></th>}
                </tr>
              </thead>
              <tbody>
                {filteredUpdates.length === 0 && (
                  <tr><td colSpan={canEdit ? 9 : 8} className="text-center py-8 text-zinc-500 text-xs">
                    {recentUpdates.length === 0 ? "No updates yet." : "No updates match the active filters."}
                  </td></tr>
                )}
                {filteredUpdates.map((e) => {
                  const tVal = e.installations_completed_t ?? e.installations_completed ?? 0;
                  const ntVal = e.installations_completed_nt ?? 0;
                  const camp = campaignById[e.campaign_id];
                  const label = camp ? campaignDisplayLabel(camp) : e.campaign_id;
                  return (
                    <tr key={e.id} className="border-b border-zinc-100 hover:bg-zinc-50 group" data-testid={`recent-update-${e.id}`}>
                      <td className="py-2.5 px-3 font-mono text-xs">{e.update_date}</td>
                      <td className="py-2.5 px-3 text-xs" title={e.campaign_id}>
                        <div className="font-medium">{label}</div>
                        <div className="font-mono text-[10px] text-zinc-400">{e.campaign_id}</div>
                      </td>
                      <td className="py-2.5 px-3">{e.installations_planned}</td>
                      <td className="py-2.5 px-3 font-semibold">{tVal}</td>
                      <td className="py-2.5 px-3">{ntVal}</td>
                      <td className="py-2.5 px-3">{e.pending_onsite_count}</td>
                      <td className="py-2.5 px-3 font-mono text-xs">{e.daily_target_vs_actual_percent}%</td>
                      <td className="py-2.5 px-3">
                        {e.predictive_risk_flag ? <StatusPill status="red">Risk</StatusPill> : <StatusPill status="green">OK</StatusPill>}
                      </td>
                      {canEdit && (
                        <td className="py-2.5 px-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="p-1 hover:bg-zinc-200 rounded opacity-60 group-hover:opacity-100 transition-opacity"
                                data-testid={`update-actions-${e.id}`}
                              >
                                <MoreHorizontal className="h-3.5 w-3.5 text-zinc-500" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => setEditing(e)} data-testid={`update-edit-${e.id}`}>
                                <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                              </DropdownMenuItem>
                              {canDelete && (
                                <DropdownMenuItem onSelect={() => removeUpdate(e.id)} className="text-red-600" data-testid={`update-delete-${e.id}`}>
                                  <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              {filteredUpdates.length > 0 && (() => {
                const totals = filteredUpdates.reduce((acc, e) => {
                  const tVal = e.installations_completed_t ?? e.installations_completed ?? 0;
                  const ntVal = e.installations_completed_nt ?? 0;
                  acc.plan += Number(e.installations_planned) || 0;
                  acc.t += Number(tVal) || 0;
                  acc.nt += Number(ntVal) || 0;
                  acc.onsite += Number(e.pending_onsite_count) || 0;
                  return acc;
                }, { plan: 0, t: 0, nt: 0, onsite: 0 });
                return (
                  <tfoot className="bg-zinc-50 border-t border-zinc-300 text-xs uppercase tracking-wider" data-testid="recent-updates-totals">
                    <tr className="font-semibold">
                      <td className="py-2.5 px-3 text-zinc-600">Total</td>
                      <td className="py-2.5 px-3 text-zinc-400">{filteredUpdates.length} {filteredUpdates.length === recentUpdates.length ? "entries" : `of ${recentUpdates.length}`}</td>
                      <td className="py-2.5 px-3 font-mono text-zinc-900" data-testid="totals-plan">{totals.plan}</td>
                      <td className="py-2.5 px-3 font-mono text-zinc-900" data-testid="totals-t">{totals.t}</td>
                      <td className="py-2.5 px-3 font-mono text-zinc-900" data-testid="totals-nt">{totals.nt}</td>
                      <td className="py-2.5 px-3 font-mono text-zinc-900" data-testid="totals-onsite">{totals.onsite}</td>
                      <td className="py-2.5 px-3" colSpan={canEdit ? 3 : 2}></td>
                    </tr>
                  </tfoot>
                );
              })()}
            </table>
          </div>
        </Card>
      </div>

      {editing && (
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <EditExecutionDialog
            execution={editing}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); refresh(); }}
          />
        </Dialog>
      )}
    </div>
  );
}

function EditExecutionDialog({ execution, onClose, onSaved }) {
  const [form, setForm] = useState({
    update_date: execution.update_date || "",
    installations_planned: execution.installations_planned ?? 0,
    installations_completed_t: execution.installations_completed_t ?? execution.installations_completed ?? 0,
    installations_completed_nt: execution.installations_completed_nt ?? 0,
    pending_onsite_count: execution.pending_onsite_count ?? 0,
    pending_onsite_touched: false,
    remarks: execution.remarks || "",
  });
  const [saving, setSaving] = useState(false);

  // Re-derive Total Pending = Planned − T − NT until the user explicitly edits it.
  // Allowed to be negative (means over-delivered).
  useEffect(() => {
    setForm((f) => {
      if (f.pending_onsite_touched) return f;
      const planned = Number(f.installations_planned) || 0;
      const t = Number(f.installations_completed_t) || 0;
      const nt = Number(f.installations_completed_nt) || 0;
      const computed = planned - t - nt;
      if (computed === f.pending_onsite_count) return f;
      return { ...f, pending_onsite_count: computed };
    });
  }, [form.installations_planned, form.installations_completed_t, form.installations_completed_nt, form.pending_onsite_touched]);

  const planned = Number(form.installations_planned) || 0;
  const ct = Number(form.installations_completed_t) || 0;
  const cnt = Number(form.installations_completed_nt) || 0;
  const onsite = Number(form.pending_onsite_count) || 0;
  const completed = ct + cnt;

  const formError = "";  // Completed is allowed to exceed Planned per business rule

  const submit = async () => {
    if (formError) { toast.error(formError); return; }
    setSaving(true);
    try {
      await api.patch(`/executions/${execution.id}`, {
        update_date: form.update_date,
        installations_planned: planned,
        installations_completed_t: ct,
        installations_completed_nt: cnt,
        pending_onsite_count: onsite,
        remarks: form.remarks,
      });
      toast.success("Daily update saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <DialogContent className="max-w-lg" data-testid="edit-execution-dialog">
      <DialogHeader>
        <DialogTitle className="font-display">
          Edit daily update · <span className="font-mono text-sm text-zinc-500">{execution.campaign_id}</span>
        </DialogTitle>
      </DialogHeader>
      <p className="text-xs text-zinc-500 -mt-2">Editing this entry will adjust the campaign's total completed installations by the delta.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
        <div>
          <Label className="text-xs">Date</Label>
          <Input type="date" value={form.update_date} onChange={(e) => setForm({ ...form, update_date: e.target.value })} data-testid="edit-exec-date" />
        </div>
        <div>
          <Label className="text-xs">Planned</Label>
          <Input type="number" min={0} value={form.installations_planned} onChange={(e) => setForm({ ...form, installations_planned: e.target.value })} data-testid="edit-exec-planned" />
        </div>
        <div>
          <Label className="text-xs">Completed T <span className="text-zinc-400 font-normal">(Trusted)</span></Label>
          <Input type="number" min={0} value={form.installations_completed_t} onChange={(e) => setForm({ ...form, installations_completed_t: e.target.value })} data-testid="edit-exec-completed-t" />
        </div>
        <div>
          <Label className="text-xs">Completed N.T <span className="text-zinc-400 font-normal">(Non-Trusted)</span></Label>
          <Input type="number" min={0} value={form.installations_completed_nt} onChange={(e) => setForm({ ...form, installations_completed_nt: e.target.value })} data-testid="edit-exec-completed-nt" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Total Pending (on-site) <span className="text-zinc-400 font-normal">(auto = Planned − T − N.T)</span></Label>
          <Input type="number" value={form.pending_onsite_count} onChange={(e) => setForm({ ...form, pending_onsite_count: e.target.value, pending_onsite_touched: true })} data-testid="edit-exec-onsite" />
          {Number(form.pending_onsite_count) < 0 && (
            <p className="text-[10px] text-amber-700 mt-0.5">Negative — over-delivered by {Math.abs(Number(form.pending_onsite_count))}.</p>
          )}
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Remarks</Label>
          <Textarea rows={3} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="edit-exec-remarks" />
        </div>
      </div>
      {formError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 mt-2" data-testid="edit-exec-form-error">{formError}</div>
      )}
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onClose} data-testid="edit-exec-cancel">Cancel</Button>
        <Button onClick={submit} disabled={saving || !!formError} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="edit-exec-submit">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
